import {
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Check, Copy } from 'lucide-react'
import { clsx } from 'clsx'
import {
  externalBrowserHref,
  isUnsafeExternalHref,
  openExternalBrowser,
  shouldInterceptExternalBrowserClick,
} from '../lib/externalBrowser'
import { writeClipboardText } from '../lib/clipboard'
import { isAgentChatNativeApp } from '../lib/nativeExternalOpen'
import { attachmentDownloadHref, attachmentOpenPath, fetchAttachmentOpen, parseAgentChatAttachmentLink } from '../lib/attachmentDelivery'

const ATTACHMENT_ORIGIN_ALIASES = ((import.meta.env.VITE_AGENTCHAT_ATTACHMENT_ORIGINS as string | undefined) ?? '')
  .split(',').map((origin) => origin.trim()).filter(Boolean)

/**
 * Markdown renderer for agent / system message bubbles.
 *
 * What it parses:
 *  - GFM (`remark-gfm`): tables, strikethrough, autolinks, task lists,
 *    fenced code blocks
 *  - Standard CommonMark: headings, bold, italic, lists, blockquotes,
 *    inline code
 *
 * Design constraints:
 *  - Bubble width is owned by MessageList; prose wraps and only wide
 *    tables / <pre> blocks scroll horizontally.
 *  - Tap targets must stay finger-friendly on phone — `<a>` links
 *    open in a new tab and underline so they read as actionable.
 *  - Tailwind classes are applied per-element via `components` so
 *    we don't depend on @tailwindcss/typography plugin (which would
 *    bloat the bundle for marginal gain).
 *
 * Was the bug Mark hit: outbound bubbles rendered with literal
 * `backticks`, **stars**, and `## hashes` because the previous renderer
 * was just `<div className="whitespace-pre-wrap">{m.body}</div>`.
 * Long Codex replies looked like raw text.
 */
export default function MarkdownBody({ body }: { body: string }) {
  return (
    <div className="markdown-body">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Headings — visually distinct from body, but compact (chat
          // bubble, not a doc page).
          h1: ({ children }) => <h1 className="text-base font-bold mt-2 mb-1 first:mt-0">{children}</h1>,
          h2: ({ children }) => <h2 className="text-base font-semibold mt-2 mb-1 first:mt-0">{children}</h2>,
          h3: ({ children }) => <h3 className="text-sm font-semibold mt-2 mb-1 first:mt-0">{children}</h3>,
          h4: ({ children }) => <h4 className="text-sm font-semibold mt-1.5 mb-1 first:mt-0">{children}</h4>,
          h5: ({ children }) => <h5 className="text-sm font-medium mt-1.5 mb-1 first:mt-0">{children}</h5>,
          h6: ({ children }) => <h6 className="text-sm font-medium mt-1.5 mb-1 first:mt-0">{children}</h6>,
          // Paragraphs — preserve breathing room between, but tight
          // enough that one-liners don't waste vertical space.
          p: ({ children }) => <p className="my-1 first:mt-0 last:mb-0 leading-relaxed">{children}</p>,
          // Lists — tighten Tailwind defaults; bullets stay inside the
          // bubble's padding.
          ul: ({ children }) => <ul className="list-disc pl-5 my-1 space-y-0.5">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal pl-5 my-1 space-y-0.5">{children}</ol>,
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,
          // Inline code — subtle rounded chip, monospace.
          code: ({ className, children, ...props }) => {
            const isBlock = (className ?? '').includes('language-')
            if (isBlock) {
              return (
                <code className={`${className} font-mono text-[0.85em]`} {...props}>
                  {children}
                </code>
              )
            }
            return (
              <code
                className="font-mono text-[0.88em] px-1 py-0.5 rounded bg-stone-200/80 dark:bg-stone-700/60 text-stone-900 dark:text-stone-100"
                {...props}
              >
                {children}
              </code>
            )
          },
          // Code blocks — full-width-ish, horizontal scroll for wide
          // lines, with a one-tap Copy button overlay so Mark can
          // copy commands without selecting text by hand. Crucial when
          // extreme-length prose has moved to an attachment but the
          // code stays in the bubble.
          pre: ({ children }) => <CodeBlock>{children}</CodeBlock>,
          // Block quotes
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-stone-300 dark:border-stone-600 pl-3 my-1.5 italic text-stone-700 dark:text-stone-300">
              {children}
            </blockquote>
          ),
          // Links — route every tap through the same external-open path used by
          // document chips, so the APK WebView never has to interpret target=_blank.
          a: ({ href, children }) => <MarkdownLink href={href}>{children}</MarkdownLink>,
          img: ({ src, alt }) => <MarkdownImage src={src} alt={alt} />,
          // Tables — small, scrollable. GFM only.
          table: ({ children }) => (
            <div className="my-2 overflow-x-auto">
              <table className="text-[0.9em] border-collapse">{children}</table>
            </div>
          ),
          th: ({ children }) => (
            <th className="border border-stone-300 dark:border-stone-600 px-2 py-1 bg-stone-100 dark:bg-stone-800 font-semibold text-left">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="border border-stone-300 dark:border-stone-700 px-2 py-1">
              {children}
            </td>
          ),
          // Horizontal rule
          hr: () => <hr className="my-2 border-stone-300 dark:border-stone-700" />,
          // Strong / em — already styled by browser default; tweak to
          // match bubble text weight.
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
        }}
      >
        {body}
      </ReactMarkdown>
    </div>
  )
}

function isLocalFilesystemHref(href: string | undefined): boolean {
  const value = (href ?? '').trim().toLowerCase()
  return value.startsWith('/home/') || value.startsWith('/tmp/') || value.startsWith('file:')
}

function isRemoteImageSrc(src: string | undefined): boolean {
  const target = (src ?? '').trim()
  if (!target || isUnsafeExternalHref(target) || isLocalFilesystemHref(target)) return false
  try {
    const parsed = new URL(target)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

function MarkdownImage({
  src,
  alt,
}: {
  src: string | undefined
  alt: string | undefined
}) {
  const target = (src ?? '').trim()
  if (!isRemoteImageSrc(target)) {
    return alt ? (
      <span className="text-stone-500 dark:text-stone-400">
        {alt}
      </span>
    ) : null
  }
  return (
    <img
      src={target}
      alt={alt ?? ''}
      loading="lazy"
      referrerPolicy="no-referrer"
      className="my-2 block max-h-56 w-full max-w-full rounded-lg border border-stone-200/70 bg-stone-100 object-cover dark:border-stone-700 dark:bg-stone-900"
    />
  )
}

function MarkdownLink({
  href,
  children,
}: {
  href: string | undefined
  children: ReactNode
}) {
  const [openError, setOpenError] = useState<string | null>(null)
  const target = (href ?? '').trim()
  const attachment = typeof window === 'undefined'
    ? null : parseAgentChatAttachmentLink(target, window.location.origin, ATTACHMENT_ORIGIN_ALIASES)
  const nativeAttachment = !!attachment && isAgentChatNativeApp()
  const blockedTitle = isLocalFilesystemHref(target)
    ? 'Local file path was not attached'
    : 'Link blocked'
  if (!target || isUnsafeExternalHref(target) || isLocalFilesystemHref(target)) {
    return (
      <span
        className="underline underline-offset-2 decoration-stone-400 text-stone-500 dark:text-stone-400"
        title={blockedTitle}
      >
        {children}
      </span>
    )
  }
  const interceptClick = attachment ? nativeAttachment : shouldInterceptExternalBrowserClick(target)

  function stopBubbleGesture(event: ReactPointerEvent<HTMLAnchorElement>) {
    event.stopPropagation()
  }

  async function openLink(event: ReactMouseEvent<HTMLAnchorElement>) {
    event.stopPropagation()
    if (!interceptClick) return
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return
    }
    event.preventDefault()
    setOpenError(null)
    try {
      // The external browser may not share the app's cookie jar. Mint a
      // short-lived capability while still inside the authenticated app.
      if (attachment) {
        const meta = await fetchAttachmentOpen(attachment.id)
        await openExternalBrowser(attachment.download ? meta.fallback_url : meta.file_url)
      } else {
        await openExternalBrowser(target)
      }
    } catch (error) {
      setOpenError(error instanceof Error ? error.message : 'Could not open the file. Please retry.')
    }
  }

  return (
    <>
    <a
      href={attachment
        ? (attachment.download ? attachmentDownloadHref(attachment.id) : attachmentOpenPath(attachment.id))
        : externalBrowserHref(target)}
      target={interceptClick ? undefined : '_blank'}
      rel="noopener noreferrer"
      onPointerDown={stopBubbleGesture}
      onClick={openLink}
      className="underline underline-offset-2 decoration-stone-400 hover:decoration-current cursor-pointer select-auto"
    >
      {children}
    </a>
    {openError && <span role="alert" className="ml-1 text-xs text-red-600">{openError}</span>}
    </>
  )
}

/**
 * Code block + copy button. Wraps the standard `<pre>` in a relative
 * container, reads the rendered text on demand (so we always copy
 * exactly what's on screen, including any whitespace fixes ReactMarkdown
 * applied), and flashes a check mark for ~1.5 s on success.
 *
 * Falls back to a textarea + execCommand('copy') when navigator.clipboard
 * is unavailable — keeps the button useful inside http:// previews and
 * native WebView contexts that gate the async clipboard API.
 */
function CodeBlock({ children }: { children: ReactNode }) {
  const preRef = useRef<HTMLPreElement>(null)
  const [copied, setCopied] = useState(false)

  async function copy() {
    const text = preRef.current?.innerText ?? ''
    if (!text) return
    if (await writeClipboardText(text)) {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
      return
    }
    console.warn('copy failed')
  }

  return (
    <div className="relative my-2 group">
      <pre
        ref={preRef}
        className="overflow-x-auto rounded-md bg-stone-900 dark:bg-black text-stone-50 p-2.5 pr-12 text-[0.85em] leading-relaxed"
      >
        {children}
      </pre>
      <button
        type="button"
        onClick={copy}
        aria-label={copied ? 'Copied' : 'Copy code'}
        title={copied ? 'Copied!' : 'Copy code'}
        className={clsx(
          'absolute top-1.5 right-1.5 grid place-items-center w-8 h-8 rounded-md',
          'bg-stone-800/80 hover:bg-stone-700 active:scale-95 transition',
          'text-stone-300 hover:text-white',
          // Keep the button visible on mobile (no hover); subtler on
          // desktop until the bubble is hovered.
          'opacity-90 sm:opacity-60 sm:group-hover:opacity-100',
          copied && 'bg-emerald-600/90 text-white opacity-100',
        )}
      >
        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  )
}
